import axios, { AxiosError } from "axios";
import { useNetworkStatusStore } from "components/store/networkStatusStore";

const HEALTH_CHECK_TIMEOUT_MS = 8_000;

function getApiBaseUrl(): string {
  const fromStorage = localStorage.getItem("queueAPI");
  if (fromStorage?.trim()) {
    return fromStorage.replace(/\/$/, "");
  }

  const fromEnv = import.meta.env.VITE_AUTHENTICATION_API;
  return fromEnv?.trim() ? fromEnv.replace(/\/$/, "") : "";
}

export function isNetworkOrServerError(error: unknown): boolean {
  if (!axios.isAxiosError(error)) {
    return false;
  }

  const axiosError = error as AxiosError;
  if (!axiosError.response) {
    return true;
  }

  const status = axiosError.response.status;
  return status >= 500 || status === 408;
}

export function markBackendReachable(): void {
  const store = useNetworkStatusStore.getState();
  store.setBrowserOnline(typeof navigator === "undefined" ? true : navigator.onLine);
  store.setBackendReachable(true);
  store.setChecking(false);
  store.markChecked();
}

export function markBackendUnreachable(): void {
  const store = useNetworkStatusStore.getState();
  if (typeof navigator !== "undefined" && !navigator.onLine) {
    store.setBrowserOnline(false);
  }
  store.setBackendReachable(false);
  store.setChecking(false);
  store.markChecked();
}

export async function pingApiHealth(): Promise<boolean> {
  const store = useNetworkStatusStore.getState();

  if (typeof navigator !== "undefined" && !navigator.onLine) {
    store.setBrowserOnline(false);
    store.setBackendReachable(false);
    store.setChecking(false);
    store.markChecked();
    return false;
  }

  store.setBrowserOnline(true);
  store.setChecking(true);

  const baseURL = getApiBaseUrl();
  if (!baseURL) {
    store.setBackendReachable(false);
    store.setChecking(false);
    store.markChecked();
    return false;
  }

  try {
    await axios.get(`${baseURL}/openapi/v1.json`, {
      timeout: HEALTH_CHECK_TIMEOUT_MS,
      validateStatus: (status) => status < 500,
    });
    markBackendReachable();
    return true;
  } catch {
    markBackendUnreachable();
    return false;
  }
}
